/*
 * changed:
 *  - set width of autosuggest box
 *  - added css class if desktop form
 */

import {onDomReady} from './helpers/on-dom-ready'

function suggestFetch(url, query) {
  const qs = 'tx_solr[queryString]=' + encodeURIComponent(query)
  return fetch(url + (url.includes('?') ? '&' : '?') + qs)
    .then(function (r) { return r.json() })
}

function escapeRegExChars(value) {
  return value.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
}

function SuggestController() {

  const initializedForms = new WeakSet()

  this.init = function () {

    document.querySelectorAll('form[data-suggest]').forEach(function (form) {
      if (initializedForms.has(form)) return
      initializedForms.add(form)

      const suggestInput = form.querySelector('.tx-solr-suggest')
      if (!suggestInput) return

      // changed: determine if this is a desktop topbar form
      const formIsDesktop = form.classList.contains('search__form--topbar')

      form.querySelector('.tx-solr-suggest-focus')?.focus()

      let searchBox = suggestInput
      if (!form.querySelector('.tx-solr-suggest')) {
        searchBox = form
      }
      suggestInput.parentNode.style.position = 'relative'

      // Prevent submit of empty search form
      form.addEventListener('submit', function (e) {
        if (suggestInput.value === '') {
          e.preventDefault()
          suggestInput.focus()
        }
      })

      // Build suggestions container — CSS classes match devbridge output
      // so that header.js MutationObserver keeps working unchanged
      const container = document.createElement('div')
      container.className = 'autocomplete-suggestions'
      container.style.cssText = 'display:none; position:absolute; overflow:auto; z-index:9999;'
      container.style.maxHeight = '1000px'
      suggestInput.parentNode.insertBefore(container, suggestInput.nextSibling)

      let renderedSuggestions = []
      let selectedIndex = -1
      let debounceTimer = null

      function transformResult(response) {
        if (!response.suggestions) return {suggestions: []}
        let firstSuggestion
        // $.map with object passes (value, key) — Object.entries gives [key, value]
        const result = {
          suggestions: Object.entries(response.suggestions).map(([suggestion, count]) => {
            if (!firstSuggestion) firstSuggestion = suggestion
            return {value: suggestion, data: {category: 'suggestion', count: count}}
          })
        }

        ;(response.documents || []).forEach(function (value) {
          const dataObject = Object.assign({}, value)
          const defaultGroup = form.getAttribute('data-suggest-header') || 'Top results'
          dataObject.category = defaultGroup

          if (dataObject.group) {
            // getAttribute keeps hyphens as-is; equivalent to jQuery's $form.data('suggest-header-{group}')
            dataObject.category = form.getAttribute('data-suggest-header-' + dataObject.group) || dataObject.group
          }

          result.suggestions.push({value: firstSuggestion, data: dataObject})
        })

        return result
      }

      function formatResult(suggestion, currentValue) {
        if (!currentValue) return suggestion.value
        const pattern = '(' + escapeRegExChars(currentValue.trim()) + ')'

        if (suggestion.data.category === 'suggestion') {
          return suggestion.value
            .replace(new RegExp(pattern, 'gi'), '<strong>$1</strong>')
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
            .replace(/&lt;(\/?strong)&gt;/g, '<$1>')
        } else {
          const title = (suggestion.data.title || '')
            .replace(new RegExp(pattern, 'gi'), '<em>$1</em>')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/&lt;(\/?em)&gt;/g, '<$1>')

          return '<div class="' + suggestion.data.type + '">' +
            (suggestion.data.previewImage
              ? '<figure' + (suggestion.data.hasVideo ? ' class="hasVideo"' : '') + '><img src="' + suggestion.data.previewImage + '" /></figure>'
              : '') +
            '<a href="' + suggestion.data.link + '" class="internal-link">' + title + '</a>' +
            '</div>'
        }
      }

      function renderSuggestions(currentValue, suggestions) {
        container.innerHTML = ''
        renderedSuggestions = []
        selectedIndex = -1

        if (!suggestions.length) {
          hideSuggestions()
          return
        }

        // group by category, preserving insertion order
        const groups = new Map()
        for (const s of suggestions) {
          const cat = s.data?.category || ''
          if (!groups.has(cat)) groups.set(cat, [])
          groups.get(cat).push(s)
        }

        for (const [cat, items] of groups) {
          const groupEl = document.createElement('div')
          groupEl.className = 'autocomplete-group'
          groupEl.innerHTML = '<strong>' + cat + '</strong>'
          container.appendChild(groupEl)

          for (const s of items) {
            const itemEl = document.createElement('div')
            itemEl.className = 'autocomplete-suggestion'
            itemEl.innerHTML = formatResult(s, currentValue)
            itemEl.addEventListener('mousedown', function (e) {
              e.preventDefault() // keep focus on input so blur doesn't hide before click fires
              selectSuggestion(s)
            })
            container.appendChild(itemEl)
            renderedSuggestions.push(s)
          }
        }

        // changed: beforeRender logic (was jQuery callback, inlined here)
        container.querySelector('.autocomplete-group')?.remove() // remove first group header
        container.classList.add('tx-solr-autosuggest')

        if (formIsDesktop) {
          container.classList.add('tx-solr-autosuggest--desktop')
          container.style.width = searchBox.offsetWidth + 'px'
        } else {
          container.style.width = form.offsetWidth + 'px'
        }

        container.style.top = (suggestInput.offsetTop + suggestInput.offsetHeight) + 'px'
        container.style.left = suggestInput.offsetLeft + 'px'

        searchBox.parentElement.classList.add('autocomplete-active')
        container.style.display = 'block'
      }

      function hideSuggestions() {
        container.style.display = 'none'
        searchBox.parentElement.classList.remove('autocomplete-active')
        selectedIndex = -1
        renderedSuggestions = []
      }

      function updateSelection() {
        container.querySelectorAll('.autocomplete-suggestion').forEach((el, i) => {
          el.classList.toggle('autocomplete-selected', i === selectedIndex)
        })
      }

      function selectSuggestion(suggestion) {
        suggestInput.value = suggestion.value
        hideSuggestions()

        if (suggestion.data?.link) {
          if (suggestion.data.link.indexOf('https://www.youtube.com') === 0) {
            openVideoOverlay(suggestion.data.link)
          } else {
            location.href = suggestion.data.link
          }
        } else {
          form.requestSubmit()
        }
      }

      suggestInput.addEventListener('input', function () {
        const value = this.value
        clearTimeout(debounceTimer)
        if (!value) {
          hideSuggestions()
          return
        }
        debounceTimer = setTimeout(function () {
          suggestFetch(form.getAttribute('data-suggest'), value)
            .then(function (response) {
              const result = transformResult(response)
              renderSuggestions(value, result.suggestions)
            })
            .catch(function () {})
        }, 300)
      })

      suggestInput.addEventListener('keydown', function (e) {
        const items = container.querySelectorAll('.autocomplete-suggestion')
        if (!items.length) return

        if (e.key === 'ArrowDown') {
          e.preventDefault()
          selectedIndex = Math.min(selectedIndex + 1, items.length - 1)
          updateSelection()
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          selectedIndex = Math.max(selectedIndex - 1, -1)
          updateSelection()
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
          e.preventDefault()
          selectSuggestion(renderedSuggestions[selectedIndex])
        } else if (e.key === 'Escape') {
          hideSuggestions()
        }
      })

      suggestInput.addEventListener('blur', function () {
        searchBox.parentElement.classList.remove('autocomplete-active')
        setTimeout(hideSuggestions, 200)
      })
    })
  }
}

onDomReady(function () {
  /** solr search autocomplete **/
  var solrSuggestController = new SuggestController()
  solrSuggestController.init()

  document.body.addEventListener('tx_solr_updated', function () {
    solrSuggestController.init()
  })
})
